import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';
import {
  PURCHASE_PAYMENT_RECORD_STATUS_LABEL,
  PURCHASE_PAYMENT_RECORD_TYPE_LABEL,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchasePaymentRecord';

const STATUS_TONE = {
  DRAFT: 'text-amber-700',
  CONFIRMED: 'text-emerald-700',
  VOIDED: 'text-gray-400 line-through',
};

const CELL = 'px-3 py-2 text-sm tabular-nums';

function allocationText(allocations, phaseNoById) {
  if (!allocations?.length) {
    return '—';
  }
  return allocations
    .map((a) => `第 ${phaseNoById.get(a.phaseId) ?? '?'} 期 ${formatDisplayAmount(a.amount)}`)
    .join('、');
}

/**
 * 財務付款／退款紀錄表（`fms.purchase_payment.read`）。期別編號由目前計畫的 `phases` 對照；
 * 舊版計畫的期別對不到時顯示「?」。`renderActions(record)` 由呼叫端依權限決定。
 */
const PurchasePaymentRecordsTable = ({ records = [], phases = [], renderActions }) => {
  if (records.length === 0) {
    return <p className="text-sm text-muted-foreground">尚無付款紀錄</p>;
  }

  const phaseNoById = new Map(phases.map((p) => [p.id, p.phaseNo]));

  return (
    <div className="overflow-x-auto rounded-md border border-gray-300">
      <table className="w-full min-w-[44rem] border-collapse">
        <thead className="bg-gray-100 text-xs font-medium text-gray-600">
          <tr>
            <th className={cn(CELL, 'text-left')}>類型</th>
            <th className={cn(CELL, 'text-left')}>狀態</th>
            <th className={cn(CELL, 'text-right')}>金額</th>
            <th className={cn(CELL, 'text-left')}>實際時間</th>
            <th className={cn(CELL, 'text-left')}>期別分配</th>
            <th className={cn(CELL, 'text-left')}>備註</th>
            {renderActions ? <th className={cn(CELL, 'text-right')}>操作</th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {records.map((record) => (
            <tr key={record.id}>
              <td className={cn(CELL, 'text-left')}>
                {PURCHASE_PAYMENT_RECORD_TYPE_LABEL[record.recordType] ?? record.recordType}
              </td>
              <td className={cn(CELL, 'text-left', STATUS_TONE[record.status])}>
                {PURCHASE_PAYMENT_RECORD_STATUS_LABEL[record.status] ?? record.status}
              </td>
              <td className={cn(CELL, 'text-right', record.recordType === 'REFUND' && 'text-rose-600')}>
                {record.recordType === 'REFUND' ? '−' : ''}
                {formatDisplayAmount(record.amount)}
              </td>
              <td className={cn(CELL, 'text-left')}>{formatDateTimeOrDash(record.actualPaidAt)}</td>
              <td className={cn(CELL, 'text-left')}>{allocationText(record.allocations, phaseNoById)}</td>
              <td className={cn(CELL, 'max-w-[16rem] truncate text-left')} title={record.remark ?? ''}>
                {record.remark || '—'}
              </td>
              {renderActions ? (
                <td className={cn(CELL, 'whitespace-nowrap text-right')}>{renderActions(record)}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

PurchasePaymentRecordsTable.propTypes = {
  records: PropTypes.arrayOf(PropTypes.object),
  phases: PropTypes.arrayOf(PropTypes.object),
  renderActions: PropTypes.func,
};

export default PurchasePaymentRecordsTable;
